import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Toaster, toast } from 'sonner';
import { Star, ArrowLeft, CircleNotch } from '@phosphor-icons/react';
import { useNavigate } from 'react-router-dom';
import { ExportButton } from '../components/ExportCSV';
import WindowNarrative from '../components/WindowNarrative';
import { NARRATIVES } from '../components/windowNarratives';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PhaseBadge = ({ phase }) => {
  const cls = phase === 'CHAOTIC' ? 'bg-red-100 text-red-800' : phase === 'RANDOM' ? 'bg-orange-100 text-orange-800' : phase === 'BALANCED' ? 'bg-green-100 text-green-800' : 'bg-blue-100 text-blue-800';
  return <span className={`inline-flex items-center px-2 py-1 rounded text-xs font-bold ${cls}`} data-testid={`phase-${phase.toLowerCase()}`}>{phase}</span>;
};

const ConfidenceBadge = ({ confidence }) => {
  const cls = confidence === 'HIGH' ? 'bg-green-100 text-green-800' : confidence === 'MEDIUM' ? 'bg-amber-100 text-amber-800' : 'bg-red-100 text-red-800';
  return <span className={`inline-flex items-center px-2 py-1 rounded text-xs font-bold ${cls}`} data-testid={`conf-${confidence.toLowerCase()}`}>{confidence}</span>;
};

export default function Window23Entropy() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);

  useEffect(() => {
    (async () => {
      try { const res = await axios.get(`${API}/analysis/entropy`); setData(res.data); }
      catch { toast.error('Failed to fetch entropy analysis'); }
      finally { setLoading(false); }
    })();
  }, []);

  const ballLabels = { ball_1: 'Ball 1', ball_2: 'Ball 2', ball_3: 'Ball 3', ball_4: 'Ball 4', ball_5: 'Ball 5' };

  return (
    <div className="min-h-screen bg-gray-50">
      <Toaster position="top-right" richColors />
      <header className="bg-white border-b border-gray-200 sticky top-0 z-10">
        <div className="max-w-[1600px] mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center gap-3">
              <Button variant="ghost" size="sm" onClick={() => navigate('/')} data-testid="back-btn"><ArrowLeft size={18} /></Button>
              <div className="w-10 h-10 bg-[#002FA7] rounded flex items-center justify-center"><Star size={24} weight="fill" className="text-amber-400" /></div>
              <div><h1 className="text-2xl font-bold text-gray-900">Window 23</h1><p className="text-xs text-gray-500">Entropy & Randomness Score</p></div>
            </div>
          </div>

            <ExportButton data={data?.timeline} filename="w23_entropy_timeline" />
          </div>
      </header>
      <main className="max-w-[1600px] mx-auto px-4 sm:px-6 lg:px-8 py-6">

        <WindowNarrative windowId="W23" {...NARRATIVES.W23} className="mb-5" />
        {loading ? <div className="flex justify-center py-20"><CircleNotch size={32} className="animate-spin text-[#002FA7]" /></div> : data ? (
          <>
            {/* Current State */}
            <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-6">
              <div className="bg-white border rounded p-4">
                <p className="text-xs uppercase font-bold text-gray-500">Number Entropy</p>
                <p className="text-2xl font-bold text-gray-900 mt-1" data-testid="stat-entropy">{data.current_entropy}</p>
              </div>
              <div className="bg-white border rounded p-4">
                <p className="text-xs uppercase font-bold text-gray-500">Diff Entropy</p>
                <p className="text-2xl font-bold text-gray-900 mt-1">{data.current_diff_entropy}</p>
              </div>
              <div className="bg-white border rounded p-4 bg-amber-50 border-amber-200">
                <p className="text-xs uppercase font-bold text-amber-600">Randomness</p>
                <p className="text-2xl font-bold text-amber-700 mt-1" data-testid="stat-randomness">{data.randomness_pct}%</p>
              </div>
              <div className="bg-white border rounded p-4">
                <p className="text-xs uppercase font-bold text-gray-500">Current Phase</p>
                <div className="mt-2"><PhaseBadge phase={data.phase} /></div>
              </div>
              <div className="bg-white border rounded p-4">
                <p className="text-xs uppercase font-bold text-gray-500">Prediction Confidence</p>
                <div className="mt-2"><ConfidenceBadge confidence={data.confidence} /></div>
              </div>
            </div>

            {/* Randomness Gauge */}
            <div className="bg-white border rounded p-6 mb-6">
              <h3 className="font-bold text-gray-900 mb-4">Randomness Gauge (Last 20 Draws)</h3>
              <div className="relative h-8 bg-gray-100 rounded-full overflow-hidden mb-2" data-testid="randomness-gauge">
                <div className="absolute inset-0 flex">
                  <div className="h-full bg-blue-500" style={{ width: '55%' }} />
                  <div className="h-full bg-green-500" style={{ width: '15%' }} />
                  <div className="h-full bg-orange-500" style={{ width: '15%' }} />
                  <div className="h-full bg-red-500" style={{ width: '15%' }} />
                </div>
                <div className="absolute top-0 bottom-0 w-1 bg-black" style={{ left: `${data.randomness_pct}%` }} />
              </div>
              <div className="flex justify-between text-[10px] font-bold text-gray-500">
                <span>PATTERNED (0-55%)</span>
                <span>BALANCED (55-70%)</span>
                <span>RANDOM (70-85%)</span>
                <span>CHAOTIC (85-100%)</span>
              </div>
              <p className="text-sm text-gray-600 mt-4">
                {data.phase === 'CHAOTIC' && 'The current phase is highly chaotic — patterns are breaking down. Prediction confidence should be LOW.'}
                {data.phase === 'RANDOM' && 'The current phase shows high randomness — some patterns exist but are weak. Medium confidence.'}
                {data.phase === 'BALANCED' && 'The current phase is balanced — good mix of pattern and randomness. HIGH confidence predictions.'}
                {data.phase === 'PATTERNED' && 'The current phase shows strong patterns — numbers are following predictable distributions. HIGH confidence.'}
              </p>
            </div>

            {/* Per-ball entropy */}
            <div className="bg-white border rounded p-4 mb-6">
              <h3 className="font-bold text-gray-900 mb-3">Per-Ball Entropy (Last 20 Draws)</h3>
              <div className="grid grid-cols-1 md:grid-cols-5 gap-4" data-testid="ball-entropy">
                {Object.entries(ballLabels).map(([key, label]) => {
                  const be = data.ball_entropy?.[key] || {};
                  const pct = be.max_possible > 0 ? (be.entropy / be.max_possible * 100).toFixed(1) : 0;
                  return (
                    <div key={key} className="border rounded p-3">
                      <p className="text-xs font-bold text-gray-500 mb-1">{label}</p>
                      <div className="flex items-end gap-2">
                        <span className="text-xl font-mono font-bold">{be.entropy}</span>
                        <span className="text-xs text-gray-400">/ {be.max_possible}</span>
                      </div>
                      <div className="mt-2 bg-gray-100 rounded-full h-2 overflow-hidden">
                        <div className={`h-full rounded-full ${parseFloat(pct) > 80 ? 'bg-red-500' : parseFloat(pct) > 60 ? 'bg-amber-500' : 'bg-green-500'}`} style={{ width: `${pct}%` }} />
                      </div>
                      <p className="text-[10px] text-gray-500 mt-1">{pct}% random | {be.unique_numbers} unique numbers</p>
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Entropy Timeline */}
            <div className="bg-white border rounded p-4">
              <h3 className="font-bold text-gray-900 mb-3">Entropy Timeline (Rolling 20-draw windows)</h3>
              <div className="overflow-x-auto" data-testid="entropy-timeline">
                <table className="w-full text-xs">
                  <thead className="bg-gray-100">
                    <tr>
                      <th className="px-2 py-2 text-center">Window</th>
                      <th className="px-2 py-2 text-center">Num Entropy</th>
                      <th className="px-2 py-2 text-center">Diff Entropy</th>
                      <th className="px-2 py-2 text-center">Randomness</th>
                      <th className="px-2 py-2">Level</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.timeline.map((t, idx) => (
                      <tr key={idx} className={idx % 2 === 0 ? 'bg-white' : 'bg-gray-50'}>
                        <td className="px-2 py-1 border-b font-mono text-center text-[10px]">#{t.start_draw}-#{t.end_draw}</td>
                        <td className="px-2 py-1 border-b font-mono text-center">{t.num_entropy}</td>
                        <td className="px-2 py-1 border-b font-mono text-center">{t.diff_entropy}</td>
                        <td className="px-2 py-1 border-b font-mono text-center font-bold">{t.randomness_pct}%</td>
                        <td className="px-2 py-1 border-b">
                          <div className="bg-gray-100 rounded-full h-3 overflow-hidden w-24">
                            <div className={`h-full rounded-full ${t.randomness_pct > 85 ? 'bg-red-500' : t.randomness_pct > 70 ? 'bg-orange-500' : t.randomness_pct > 55 ? 'bg-green-500' : 'bg-blue-500'}`} style={{ width: `${t.randomness_pct}%` }} />
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </>
        ) : null}
      </main>
    </div>
  );
}
